/** Labels and Eastern-time helpers for the Campaign Desk screens. */
import { formatCents } from './pnl'

const TZ = 'America/New_York'

function etParts(d: Date): Record<string, string> {
  return Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(d).map((p) => [p.type, p.value]),
  )
}

/** A `datetime-local` value (YYYY-MM-DDTHH:MM) showing `d` in Eastern. */
export function easternInput(d: Date): string {
  const p = etParts(d)
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`
}

/** Minutes Eastern is ahead of UTC at instant `ms` (−240 in summer, −300 in winter). */
function offsetMinutes(ms: number): number {
  const p = etParts(new Date(ms))
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second)
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000)
}

/** An Eastern wall-clock `datetime-local` value as ISO with its offset: "2026-10-20T09:30:00-04:00". */
export function easternToIso(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local)
  if (!m) return null
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
  // Guess with the offset at the wall time read as UTC, then correct once for a DST edge.
  let off = offsetMinutes(wall)
  off = offsetMinutes(wall - off * 60000)
  const sign = off <= 0 ? '-' : '+'
  const a = Math.abs(off)
  const hh = String(Math.floor(a / 60)).padStart(2, '0')
  const mm = String(a % 60).padStart(2, '0')
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:00${sign}${hh}:${mm}`
}

/** "Thu Oct 16, 6:00 AM ET". */
export function fmtEastern(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }) + ' ET'
}

function humanize(s: string): string {
  const w = s.replace(/_/g, ' ').trim()
  return w.charAt(0).toUpperCase() + w.slice(1)
}

const STATUS: Record<string, string> = {
  queued: 'Queued', drafting: 'Drafting', ready: 'Ready for review', changes_requested: 'Changes asked for',
  approved: 'Approved', running: 'Sending', paused: 'Paused', completed: 'Completed', stopped: 'Stopped',
  rejected: 'Rejected', failed: 'Draft failed',
}
export const STATUSES = Object.keys(STATUS)
export const statusLabel = (s: string): string => STATUS[s] ?? humanize(s)

/** Chip colours: amber needs the operator, green is live, grey is finished. */
export function statusTone(s: string): 'action' | 'live' | 'done' | 'bad' | 'wait' {
  if (s === 'ready' || s === 'paused') return 'action'
  if (s === 'approved' || s === 'running') return 'live'
  if (s === 'failed') return 'bad'
  if (s === 'queued' || s === 'drafting' || s === 'changes_requested') return 'wait'
  return 'done'
}

const PAUSE: Record<string, string> = {
  agent_off: 'the agent is switched off',
  over_budget: 'the next wave would go over budget',
  budget: 'the budget is spent',
}
export function pauseLabel(reason: string | null | undefined): string {
  if (!reason) return 'Paused'
  return `Paused: ${PAUSE[reason] ?? humanize(reason).toLowerCase()}`
}

const EVENT: Record<string, string> = {
  brief_created: 'Brief entered', drafted: 'Package drafted', draft_failed: 'Draft failed',
  draft_deferred: 'Draft put off', changes_requested: 'Changes asked for', copy_edited: 'Copy edited',
  rejected: 'Rejected', approved: 'Approved', paused: 'Paused', halted: 'Sending halted',
  wave_sent: 'Wave sent', wave_rewritten: 'Follow-up rewritten', wave_rewrite_skipped: 'Follow-up rewrite skipped',
  stopped: 'Stopped', resumed: 'Resumed', completed: 'Completed', campaign_failed: 'Campaign failed',
}
export const eventLabel = (k: string): string => EVENT[k] ?? humanize(k)

const TARGET: Record<string, string> = {
  non_openers: "people who didn't open", non_clickers: "people who didn't click",
  clicked_not_bought: "people who clicked but didn't buy", not_converted: "everyone who hasn't bought",
}
export const targetLabel = (t: string): string => TARGET[t] ?? humanize(t).toLowerCase()

export function firstLine(s: string): string {
  return (s ?? '').trim().split('\n')[0].trim()
}

const TEXT_KEYS = ['reason', 'instruction', 'error', 'note', 'paused_reason', 'campaign_status']
const COUNT_KEYS: [string, string][] = [
  ['recipients', 'recipients'], ['sent_so_far', 'sent so far'], ['processed', 'processed'], ['mailed', 'mailed'],
  ['orders', 'orders'],
]
const CENTS_KEYS: [string, string][] = [['budget_cents', 'budget'], ['net_result_cents', 'net result']]

const isSet = (v: unknown): boolean => v !== undefined && v !== null && v !== ''

/** One line under a timeline event, from the keys the server's events write. `wave` is a day
 *  offset (0 = the first email); `wave_index` alone (follow-up rewrites) counts follow-ups from 0. */
export function eventDetail(detail: Record<string, unknown> | null): string {
  if (!detail) return ''
  const out: string[] = []
  if (isSet(detail.wave)) {
    const day = Number(detail.wave)
    out.push(day === 0 ? 'first email' : `day ${day}`)
  } else if (isSet(detail.wave_index)) {
    out.push(`follow-up ${Number(detail.wave_index) + 1}`)
  }
  for (const k of TEXT_KEYS) {
    if (isSet(detail[k])) out.push(`${k.replace(/_/g, ' ')}: ${String(detail[k])}`)
  }
  for (const [k, label] of COUNT_KEYS) {
    if (isSet(detail[k])) out.push(`${label}: ${Number(detail[k]).toLocaleString('en-US')}`)
  }
  for (const [k, label] of CENTS_KEYS) {
    if (isSet(detail[k])) out.push(`${label}: ${formatCents(Number(detail[k]))}`)
  }
  return out.join(' · ')
}
